import React, { useEffect, useState } from "react";
import { Wrench, RefreshCw, ShieldAlert, Play, ChevronDown, ChevronRight, Loader2, CheckCircle2, AlertCircle, History } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

/**
 * DbCleanupPanel — Admin → System → DB Cleanup.
 *
 * Diagnostic + healing tool for legacy data drift (guest orders orphaned
 * from user accounts, missing cost_at_sale snapshots, paid orders w/o JEs,
 * orphan journal entries, orphan stock ledger rows, etc.).
 *
 * Flow:
 *   1. Preview — GET /api/admin/db-cleanup/report renders every anomaly
 *      grouped by category, w/ drilldown into individual records.
 *   2. Select — admin ticks the categories they trust the fix for.
 *   3. Heal — POST /api/admin/db-cleanup/heal executes only the picked
 *      categories, then re-runs the report so the diff is visible.
 *   4. History — GET /api/admin/db-cleanup/history shows past runs.
 */
const SEVERITY_STYLES = {
    high: "bg-red-50 text-red-700 border-red-200",
    medium: "bg-amber-50 text-amber-700 border-amber-200",
    low: "bg-blue-50 text-blue-700 border-blue-200",
};

const DbCleanupPanel = () => {
    const [report, setReport] = useState(null);
    const [loading, setLoading] = useState(false);
    const [healing, setHealing] = useState(false);
    const [selected, setSelected] = useState({});
    const [expanded, setExpanded] = useState({});
    const [emailOverrides, setEmailOverrides] = useState({});
    const [history, setHistory] = useState([]);
    const [showHistory, setShowHistory] = useState(false);
    const [lastActionLog, setLastActionLog] = useState(null);

    const loadReport = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/db-cleanup/report");
            setReport(data);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Report scan failed");
        } finally {
            setLoading(false);
        }
    };

    const loadHistory = async () => {
        try {
            const { data } = await api.get("/admin/db-cleanup/history");
            setHistory(data || []);
        } catch (e) {
            toast.error("Couldn't load run history");
        }
    };

    useEffect(() => { loadReport(); }, []);

    const toggleCategory = (id) => setSelected((s) => ({ ...s, [id]: !s[id] }));
    const toggleExpand = (id) => setExpanded((s) => ({ ...s, [id]: !s[id] }));

    const selectedCategories = Object.entries(selected).filter(([, v]) => v).map(([k]) => k);

    const heal = async () => {
        if (!selectedCategories.length) {
            toast.error("Pick at least one category to heal");
            return;
        }
        if (!window.confirm(`Heal ${selectedCategories.length} categor${selectedCategories.length === 1 ? "y" : "ies"}? Actions are logged & auditable but not reversible.`)) return;
        setHealing(true);
        try {
            const { data } = await api.post("/admin/db-cleanup/heal", {
                categories: selectedCategories,
                email_overrides: emailOverrides,
            });
            setLastActionLog(data.action_log || {});
            toast.success("Heal complete — see action log below");
            setSelected({});
            setEmailOverrides({});
            await loadReport();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Heal failed");
        } finally {
            setHealing(false);
        }
    };

    return (
        <div className="space-y-6" data-testid="db-cleanup-panel">
            {/* Header */}
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <h2 className="text-2xl font-bold text-[#1C252E] flex items-center gap-2">
                        <Wrench className="w-6 h-6 text-[#B91C1C]" /> Database cleanup
                    </h2>
                    <p className="text-sm text-slate-600 mt-1 max-w-2xl">
                        Scans your live database for legacy data drift — orphaned orders, missing cost snapshots,
                        journal entries pointing at deleted records — and lets you patch each category selectively.
                        Every action is logged and auditable.
                    </p>
                </div>
                <div className="flex gap-2">
                    <button
                        onClick={() => { const next = !showHistory; setShowHistory(next); if (next) loadHistory(); }}
                        className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 text-sm font-medium"
                        data-testid="db-cleanup-history-btn"
                    >
                        <History className="w-4 h-4" /> {showHistory ? "Hide history" : "Run history"}
                    </button>
                    <button
                        onClick={loadReport}
                        disabled={loading}
                        className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 text-sm font-medium disabled:opacity-50"
                        data-testid="db-cleanup-refresh-btn"
                    >
                        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Rescan
                    </button>
                </div>
            </div>

            {/* Totals bar */}
            {report && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {Object.entries(report.totals).map(([k, v]) => (
                        <div key={k} className="rounded-lg border border-slate-200 bg-white px-4 py-3">
                            <div className="text-[11px] uppercase tracking-wider text-slate-500">{k.replace(/_/g, " ")}</div>
                            <div className="text-2xl font-semibold text-[#1C252E] mt-1">{v.toLocaleString()}</div>
                        </div>
                    ))}
                </div>
            )}

            {/* Run history modal */}
            {showHistory && (
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-4" data-testid="db-cleanup-history-panel">
                    <h3 className="text-sm font-semibold text-slate-700 mb-3">Recent heal runs</h3>
                    {history.length === 0 ? (
                        <p className="text-xs text-slate-500">No heal runs yet.</p>
                    ) : (
                        <div className="space-y-3 max-h-72 overflow-y-auto">
                            {history.map((h) => (
                                <div key={h.id} className="rounded-md border border-slate-200 bg-white p-3 text-xs">
                                    <div className="flex justify-between gap-2 text-slate-600">
                                        <span className="font-mono">{new Date(h.run_at).toLocaleString()}</span>
                                        <span className="text-slate-500">{h.admin_email}</span>
                                    </div>
                                    <div className="mt-1 text-slate-500">Categories: {(h.categories || []).join(", ")}</div>
                                    {h.action_log && (
                                        <details className="mt-1">
                                            <summary className="cursor-pointer text-[#B91C1C]">See actions ({Object.values(h.action_log).reduce((a, b) => a + (b?.length || 0), 0)})</summary>
                                            <pre className="mt-2 text-[11px] whitespace-pre-wrap bg-slate-50 rounded p-2">{JSON.stringify(h.action_log, null, 2)}</pre>
                                        </details>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Categories */}
            {loading && !report && (
                <div className="rounded-lg border border-slate-200 bg-white p-8 flex items-center justify-center text-slate-500">
                    <Loader2 className="w-5 h-5 animate-spin mr-2" /> Scanning database…
                </div>
            )}

            {report && (
                <>
                    {Object.entries(report.categories).every(([, cat]) => cat.count === 0) && (
                        <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-emerald-800 flex items-center gap-3" data-testid="db-cleanup-all-clean">
                            <CheckCircle2 className="w-5 h-5" />
                            <div>
                                <div className="font-semibold">Database is clean — no drift detected.</div>
                                <div className="text-xs text-emerald-700 mt-0.5">All orders, journal entries, and stock movements are internally consistent.</div>
                            </div>
                        </div>
                    )}

                    {Object.entries(report.categories).map(([id, cat]) => (
                        <div key={id} className="rounded-lg border border-slate-200 bg-white overflow-hidden" data-testid={`db-cleanup-cat-${id}`}>
                            <div className="flex items-start gap-3 p-4">
                                <input
                                    type="checkbox"
                                    checked={!!selected[id]}
                                    onChange={() => toggleCategory(id)}
                                    disabled={cat.count === 0 || (id === "missing_customer_email" && Object.keys(emailOverrides).length === 0)}
                                    className="mt-1 w-4 h-4 accent-[#B91C1C]"
                                    data-testid={`db-cleanup-check-${id}`}
                                />
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="font-semibold text-[#1C252E]">{cat.label}</span>
                                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[10px] uppercase font-semibold ${SEVERITY_STYLES[cat.severity] || SEVERITY_STYLES.low}`}>
                                            {cat.severity}
                                        </span>
                                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${cat.count > 0 ? "bg-slate-100 text-slate-700" : "bg-emerald-100 text-emerald-700"}`}>
                                            {cat.count} {cat.count === 1 ? "issue" : "issues"}
                                        </span>
                                    </div>
                                    <p className="text-xs text-slate-500 mt-1">{cat.helper}</p>
                                </div>
                                {cat.count > 0 && (
                                    <button
                                        onClick={() => toggleExpand(id)}
                                        className="text-slate-400 hover:text-slate-600 shrink-0"
                                        data-testid={`db-cleanup-expand-${id}`}
                                    >
                                        {expanded[id] ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
                                    </button>
                                )}
                            </div>
                            {expanded[id] && cat.count > 0 && (
                                <div className="border-t border-slate-100 bg-slate-50">
                                    <CategoryTable
                                        catId={id}
                                        items={cat.items}
                                        emailOverrides={emailOverrides}
                                        setEmailOverrides={setEmailOverrides}
                                    />
                                </div>
                            )}
                        </div>
                    ))}

                    {/* Heal bar */}
                    <div className="sticky bottom-4 z-10">
                        <div className="rounded-xl border border-slate-200 bg-white shadow-lg p-4 flex items-center justify-between gap-4 flex-wrap">
                            <div className="flex items-center gap-2 text-sm text-slate-700">
                                <ShieldAlert className="w-4 h-4 text-amber-600" />
                                <span>{selectedCategories.length} categor{selectedCategories.length === 1 ? "y" : "ies"} selected</span>
                            </div>
                            <button
                                onClick={heal}
                                disabled={healing || !selectedCategories.length}
                                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#B91C1C] text-white font-medium hover:bg-[#991B1B] disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                                data-testid="db-cleanup-heal-btn"
                            >
                                {healing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
                                Heal selected
                            </button>
                        </div>
                    </div>

                    {/* Last action log */}
                    {lastActionLog && (
                        <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4" data-testid="db-cleanup-action-log">
                            <div className="flex items-center gap-2 font-semibold text-emerald-800 mb-2">
                                <CheckCircle2 className="w-4 h-4" /> Last heal action log
                            </div>
                            <div className="space-y-3 text-xs">
                                {Object.entries(lastActionLog).map(([cat, actions]) => (
                                    <div key={cat}>
                                        <div className="font-semibold text-slate-700">{cat}</div>
                                        <ul className="mt-1 list-disc pl-5 space-y-0.5 text-slate-600">
                                            {(actions || []).map((a, i) => <li key={i}>{a}</li>)}
                                            {(!actions || !actions.length) && <li className="italic text-slate-400">no actions taken</li>}
                                        </ul>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

const CategoryTable = ({ catId, items, emailOverrides, setEmailOverrides }) => {
    // Auto-detect columns from the first item so each category shows its
    // own relevant fields without a bespoke component per case.
    const cols = Object.keys(items[0] || {}).filter((k) => k !== "fix");

    return (
        <div className="overflow-x-auto max-h-96 overflow-y-auto">
            <table className="min-w-full text-xs">
                <thead className="bg-slate-100 sticky top-0">
                    <tr>
                        {cols.map((c) => (
                            <th key={c} className="text-left px-3 py-2 font-semibold text-slate-600">{c.replace(/_/g, " ")}</th>
                        ))}
                        {catId === "missing_customer_email" && <th className="text-left px-3 py-2 font-semibold text-slate-600">correct email</th>}
                        <th className="text-left px-3 py-2 font-semibold text-slate-600">fix</th>
                    </tr>
                </thead>
                <tbody>
                    {items.slice(0, 200).map((it, idx) => (
                        <tr key={idx} className="border-t border-slate-200 hover:bg-white">
                            {cols.map((c) => (
                                <td key={c} className="px-3 py-2 text-slate-700 align-top">
                                    {typeof it[c] === "object" ? (
                                        <pre className="text-[10px] whitespace-pre-wrap">{JSON.stringify(it[c], null, 2)}</pre>
                                    ) : (
                                        <span className="font-mono">{String(it[c] ?? "—")}</span>
                                    )}
                                </td>
                            ))}
                            {catId === "missing_customer_email" && (
                                <td className="px-3 py-2">
                                    <input
                                        type="email"
                                        placeholder="new@example.com"
                                        value={emailOverrides[it.order_id] || ""}
                                        onChange={(e) => setEmailOverrides((s) => ({ ...s, [it.order_id]: e.target.value }))}
                                        className="border border-slate-300 rounded px-2 py-1 text-xs w-52"
                                        data-testid={`db-cleanup-email-fix-${it.order_id}`}
                                    />
                                </td>
                            )}
                            <td className="px-3 py-2 text-slate-500 italic">{it.fix}</td>
                        </tr>
                    ))}
                    {items.length > 200 && (
                        <tr>
                            <td colSpan={cols.length + 2} className="px-3 py-2 text-center text-slate-500 italic">
                                +{items.length - 200} more not shown — heal will apply to all.
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>
        </div>
    );
};

export default DbCleanupPanel;
